/**
 * invoicePDF.js — v2 complete rewrite
 *
 * Clean white A4 invoice. No dark backgrounds.
 * All content fits within page margins.
 * No authorised signatory (computer generated).
 * Watermark text properly transparent.
 * Company settings always reflected.
 */

// ─── Amount to Words ──────────────────────────────────────────────────────────
const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
              'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
              'Seventeen','Eighteen','Nineteen'];
const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

const numToWords = (n) => {
  n = Math.floor(n);
  if (n === 0) return 'Zero';
  let w = '';
  if (n >= 10000000) { w += numToWords(Math.floor(n/10000000)) + ' Crore '; n %= 10000000; }
  if (n >= 100000)   { w += numToWords(Math.floor(n/100000))   + ' Lakh ';  n %= 100000;   }
  if (n >= 1000)     { w += numToWords(Math.floor(n/1000))     + ' Thousand '; n %= 1000;   }
  if (n >= 100)      { w += numToWords(Math.floor(n/100))      + ' Hundred ';  n %= 100;    }
  if (n >= 20)       { w += tens[Math.floor(n/10)] + ' '; n %= 10; }
  if (n > 0)         { w += ones[n] + ' '; }
  return w.trim();
};

export const amountInWords = (amount) => {
  const rs = Math.floor(amount);
  const p  = Math.round((amount - rs) * 100);
  let out  = numToWords(rs) + ' Rupees';
  if (p > 0) out += ' and ' + numToWords(p) + ' Paise';
  return out + ' Only';
};

// ─── Main PDF Generator ───────────────────────────────────────────────────────
export const generateInvoicePDF = async (invoice, settings = {}, type = 'proforma') => {
  const { default: jsPDF }     = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const isProforma = type === 'proforma';
  const doc  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W    = 210;   // page width
  const H    = 297;   // page height
  const M    = 14;    // margin
  const CW   = W - M * 2;  // content width = 182mm

  // ── Colours (all print-friendly) ────────────────────────────────────────────
  const C_BLACK  = [30,  35,  45];   // near-black for main text
  const C_DARK   = [55,  65,  80];   // dark grey for subtext
  const C_GREY   = [110, 118, 130];  // medium grey for labels
  const C_LGREY  = [220, 224, 232];  // light grey for borders/lines
  const C_XGREY  = [246, 247, 250];  // very light grey for row fills
  const C_WHITE  = [255, 255, 255];
  const C_GOLD   = [180, 120, 20];   // dark gold (readable on white)
  const C_ACCENT = isProforma ? [180, 100, 20] : [20, 130, 70]; // orange or green

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const fmt  = (n) => 'Rs. ' + Number(n||0).toLocaleString('en-IN');
  const fmtN = (n) => Number(n||0).toLocaleString('en-IN');
  const txt  = (t, x, yy, opts = {}) => doc.text(String(t||''), x, yy, opts);
  const bold = (s) => doc.setFont('helvetica', s || 'bold');
  const norm = ()  => doc.setFont('helvetica', 'normal');
  const sz   = (n) => doc.setFontSize(n);
  const col  = (...c) => doc.setTextColor(...c);
  const fill = (...c) => doc.setFillColor(...c);
  const draw = (...c) => doc.setDrawColor(...c);
  const lw   = (n)   => doc.setLineWidth(n);

  let y = M;

  // ════════════════════════════════════════════════════════════
  // SECTION 1: HEADER — Logo + Company Name + Tagline
  // ════════════════════════════════════════════════════════════

  // Top gold accent line
  fill(...C_GOLD);
  doc.rect(M, y, CW, 1, 'F');
  y += 4;

  // Logo — centred
  const logoUrl = settings.logoUrl || '';
  if (logoUrl) {
    try {
      const imgType = logoUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(logoUrl, imgType, W/2 - 14, y, 28, 17);
      y += 20;
    } catch { /* ignore */ }
  }

  // Company name
  bold(); sz(16); col(...C_BLACK);
  txt(settings.companyName || 'DRUSSHTI', W/2, y, { align: 'center' });
  y += 5;

  // Tagline
  norm(); sz(8); col(...C_GREY);
  txt(settings.tagline || 'Managing Services the Smarter Way', W/2, y, { align: 'center' });
  y += 4;

  // Contact line
  const contacts = [
    settings.telephone && `Tel: ${settings.telephone}`,
    settings.email     && `Email: ${settings.email}`,
    settings.website,
  ].filter(Boolean);
  if (contacts.length) {
    sz(7); col(...C_GREY);
    txt(contacts.join('   |   '), W/2, y, { align: 'center' });
    y += 4;
  }

  // Bottom gold accent line
  fill(...C_GOLD);
  doc.rect(M, y, CW, 0.6, 'F');
  y += 5;

  // ════════════════════════════════════════════════════════════
  // SECTION 2: INVOICE TYPE LABEL
  // ════════════════════════════════════════════════════════════
  fill(...C_ACCENT);
  doc.rect(M, y, CW, 8, 'F');
  bold(); sz(10); col(...C_WHITE);
  txt(isProforma ? 'PROFORMA INVOICE' : 'TAX INVOICE', W/2, y + 5.5, { align: 'center' });
  y += 12;

  // ════════════════════════════════════════════════════════════
  // SECTION 3: FROM (left) | INVOICE DETAILS (right)
  // ════════════════════════════════════════════════════════════
  const sectionStartY = y;
  const halfCW = CW / 2 - 4;

  // LEFT: Company address
  bold(); sz(7); col(...C_GREY);
  txt('FROM', M, y);
  fill(...C_GOLD); doc.rect(M, y + 0.8, 10, 0.4, 'F');
  y += 4;

  bold(); sz(8.5); col(...C_BLACK);
  txt(settings.companyName || 'Drusshti Services', M, y);
  y += 4;

  norm(); sz(7.5); col(...C_DARK);
  const addrParts = [
    settings.addressLine1,
    settings.addressLine2,
    [settings.city, settings.state, settings.pincode].filter(Boolean).join(', '),
  ].filter(Boolean);

  addrParts.forEach(line => { txt(line, M, y); y += 3.8; });
  if (settings.gstNumber) { sz(7.5); col(...C_GREY); txt(`GSTIN: ${settings.gstNumber}`, M, y); y += 3.8; }
  if (settings.panNumber) { sz(7.5); col(...C_GREY); txt(`PAN: ${settings.panNumber}`, M, y); y += 3.8; }
  if (settings.telephone) { sz(7.5); col(...C_GREY); txt(`Tel: ${settings.telephone}`, M, y); y += 3.8; }

  // RIGHT: Invoice metadata
  const metaX = M + halfCW + 8;
  const metaW = halfCW;
  let   metaY = sectionStartY;

  fill(...C_XGREY); draw(...C_LGREY); lw(0.3);
  doc.roundedRect(metaX, metaY, metaW, 38, 2, 2, 'FD');
  metaY += 5;

  const metaRows = [
    ['Invoice No.',   invoice.invoiceNumber || '-'],
    ['Date',          new Date(invoice.createdAt || Date.now()).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })],
    ['Billing Period',invoice.billingPeriod?.from
      ? `${new Date(invoice.billingPeriod.from).toLocaleDateString('en-IN', {day:'2-digit', month:'short'})} - ${new Date(invoice.billingPeriod.to).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'})}`
      : 'Current Period'],
    ['Status',        invoice.status?.toUpperCase() || 'DRAFT'],
    ...(isProforma ? [] : [['Payment Date', invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '-']]),
  ];

  metaRows.forEach(([label, value]) => {
    norm(); sz(7); col(...C_GREY);
    txt(label, metaX + 4, metaY);
    bold(); sz(7.5); col(...C_BLACK);
    txt(value, metaX + metaW - 4, metaY, { align: 'right' });
    metaY += 7;
  });

  y = Math.max(y, metaY) + 5;

  // ════════════════════════════════════════════════════════════
  // SECTION 4: BILL TO
  // ════════════════════════════════════════════════════════════
  fill(...C_XGREY); draw(...C_LGREY); lw(0.3);
  doc.roundedRect(M, y, CW, 18, 2, 2, 'FD');

  bold(); sz(7); col(...C_GREY);
  txt('BILL TO', M + 4, y + 5);

  const client = invoice.userId || {};
  bold(); sz(9); col(...C_BLACK);
  txt(client.name || 'Client Name', M + 4, y + 11);

  norm(); sz(7.5); col(...C_GREY);
  const billRight = [];
  if (client.userId) billRight.push(`User ID: ${client.userId}`);
  if (client.email)  billRight.push(`Email: ${client.email}`);
  billRight.forEach((line, i) => txt(line, M + CW - 4, y + 5 + (i * 4.5), { align: 'right' }));

  y += 22;

  // ════════════════════════════════════════════════════════════
  // SECTION 5: ITEMS TABLE
  // ════════════════════════════════════════════════════════════
  const items = invoice.items || [];
  const subtotal   = Number(invoice.subtotal   || 0);
  const gstRate    = Number(settings.gstRate   || 18);
  const taxAmt     = isProforma ? 0 : Math.round(subtotal * gstRate / 100 * 100) / 100;
  const cgst       = Math.round(taxAmt / 2 * 100) / 100;
  const sgst       = cgst;
  const grandTotal = subtotal + taxAmt;

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [['#', 'Description', 'Society', 'Qty', 'Rate (Rs.)', 'Amount (Rs.)']],
    body: items.map((item, i) => [
      i + 1,
      item.module === 'year_end_accounting' ? 'Year End Accounting' : 'Society Book Module',
      item.societyName || '-',
      item.quantity || 1,
      fmtN(item.unitPrice),
      fmtN(item.total),
    ]),
    styles: {
      fontSize: 8, font: 'helvetica',
      textColor: [...C_DARK],
      lineColor: [...C_LGREY], lineWidth: 0.2,
      cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
    },
    headStyles: {
      fillColor: [...C_BLACK], textColor: [...C_WHITE],
      fontStyle: 'bold', fontSize: 8,
    },
    alternateRowStyles: { fillColor: [...C_XGREY] },
    bodyStyles: { fillColor: [...C_WHITE] },
    columnStyles: {
      0: { cellWidth: 8,  halign: 'center' },
      1: { cellWidth: 55, halign: 'left'   },
      2: { cellWidth: 45, halign: 'left'   },
      3: { cellWidth: 12, halign: 'center' },
      4: { cellWidth: 28, halign: 'right'  },
      5: { cellWidth: 34, halign: 'right', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.column.index === 5 && data.section === 'body') {
        data.cell.styles.textColor = [...C_ACCENT];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 4;

  // ════════════════════════════════════════════════════════════
  // SECTION 6: TOTALS (right-aligned box)
  // ════════════════════════════════════════════════════════════
  const totW = 80;
  const totX = M + CW - totW;

  const totRows = [
    ['Subtotal', fmt(subtotal)],
    ...(isProforma ? [] : [
      [`CGST @ ${gstRate/2}%`, fmt(cgst)],
      [`SGST @ ${gstRate/2}%`, fmt(sgst)],
    ]),
  ];

  let totY = y;
  fill(...C_XGREY); draw(...C_LGREY); lw(0.2);
  doc.rect(totX, totY, totW, totRows.length * 7 + 1, 'FD');

  totRows.forEach(([label, val]) => {
    totY += 5.5;
    norm(); sz(7.5); col(...C_GREY);
    txt(label, totX + 4, totY);
    bold(); sz(7.5); col(...C_DARK);
    txt(val, totX + totW - 4, totY, { align: 'right' });
    totY += 1.5;
  });
  totY += 1;

  // Grand Total row
  fill(...C_ACCENT);
  doc.rect(totX, totY, totW, 10, 'F');
  bold(); sz(8); col(...C_WHITE);
  txt('GRAND TOTAL', totX + 4, totY + 6.5);
  sz(9); txt(fmt(grandTotal), totX + totW - 4, totY + 6.5, { align: 'right' });
  totY += 10;

  y = Math.max(y + totRows.length * 7 + 12, totY) + 4;

  // ════════════════════════════════════════════════════════════
  // SECTION 7: AMOUNT IN WORDS
  // ════════════════════════════════════════════════════════════
  fill(255, 252, 240); draw(220, 190, 100); lw(0.4);
  doc.roundedRect(M, y, CW, 9, 1.5, 1.5, 'FD');
  bold(); sz(7.5); col(...C_GREY);
  txt('Amount in Words:', M + 4, y + 5.8);
  norm(); sz(7.5); col(...C_BLACK);
  const wordsText = amountInWords(grandTotal);
  const wrappedWords = doc.splitTextToSize(wordsText, CW - 44);
  txt(wrappedWords[0], M + 38, y + 5.8);
  y += 13;

  // ════════════════════════════════════════════════════════════
  // SECTION 8: BANK DETAILS (left) | NOTE (right)
  // ════════════════════════════════════════════════════════════
  const hasBank = settings.bankName || settings.accountNumber || settings.ifscCode;
  if (hasBank) {
    const bankX = M;
    const bankW = CW / 2 - 4;
    const bankLines = [
      settings.bankName      && `Bank: ${settings.bankName}`,
      settings.bankBranch    && `Branch: ${settings.bankBranch}`,
      settings.accountNumber && `A/C: ${settings.accountNumber}`,
      settings.ifscCode      && `IFSC: ${settings.ifscCode}`,
      settings.upiId         && `UPI: ${settings.upiId}`,
    ].filter(Boolean);

    fill(...C_XGREY); draw(...C_LGREY); lw(0.3);
    doc.roundedRect(bankX, y, bankW, bankLines.length * 4 + 9, 2, 2, 'FD');

    bold(); sz(7); col(...C_GREY);
    txt('PAYMENT DETAILS', bankX + 4, y + 5);
    fill(...C_GOLD); doc.rect(bankX + 4, y + 6, 28, 0.4, 'F');

    let bankY = y + 10;
    bankLines.forEach(line => {
      norm(); sz(7.5); col(...C_DARK);
      txt(line, bankX + 4, bankY);
      bankY += 4;
    });

    // Right side note box
    const noteX = M + CW / 2 + 4;
    const noteW = CW / 2 - 4;
    fill(...C_XGREY); draw(...C_LGREY);
    doc.roundedRect(noteX, y, noteW, bankLines.length * 4 + 9, 2, 2, 'FD');
    bold(); sz(7); col(...C_GREY);
    txt('NOTE', noteX + 4, y + 5);
    fill(...C_ACCENT); doc.rect(noteX + 4, y + 6, 10, 0.4, 'F');
    norm(); sz(7); col(...C_DARK);
    const noteLines = doc.splitTextToSize(
      isProforma
        ? 'This is a Proforma Invoice. Actual tax invoice will be issued upon confirmation of payment.'
        : 'This is a computer-generated invoice. Payment has been confirmed.',
      noteW - 8
    );
    noteLines.forEach((line, i) => txt(line, noteX + 4, y + 10 + i * 4));

    y += bankLines.length * 4 + 13;
  }

  // ════════════════════════════════════════════════════════════
  // SECTION 9: TERMS & CONDITIONS
  // ════════════════════════════════════════════════════════════
  const terms = isProforma ? (settings.proformaTerms || '') : (settings.taxInvoiceTerms || '');
  if (terms) {
    bold(); sz(7.5); col(...C_GREY);
    txt('TERMS & CONDITIONS', M, y);
    fill(...C_GOLD); doc.rect(M, y + 1, 40, 0.4, 'F');
    y += 5;
    norm(); sz(7); col(...C_GREY);
    const termLines = doc.splitTextToSize(terms, CW);
    doc.text(termLines, M, y);
    y += termLines.length * 3.5 + 3;
  }

  // ════════════════════════════════════════════════════════════
  // SECTION 10: FOOTER
  // ════════════════════════════════════════════════════════════
  // Gold line above footer
  const footerY = H - 16;
  fill(...C_LGREY);
  doc.rect(M, footerY, CW, 0.3, 'F');

  // Computer generated notice
  bold(); sz(7); col(...C_GREY);
  txt('This is a computer-generated document and does not require a physical signature.', W/2, footerY + 5, { align: 'center' });
  norm(); sz(6.5); col(170, 175, 185);
  txt(
    `Generated: ${new Date().toLocaleString('en-IN')}  |  ${settings.website || ''}`,
    W/2, footerY + 10, { align: 'center' }
  );

  // ════════════════════════════════════════════════════════════
  // WATERMARK — properly transparent, diagonal, faint
  // ════════════════════════════════════════════════════════════
  const watermarkText = isProforma ? 'PROFORMA' : 'TAX INVOICE';
  // jsPDF does not have true opacity but we can use a very light grey colour
  doc.setGState(doc.GState({ opacity: 0.06 }));
  bold(); sz(52); col(100, 100, 100);
  doc.text(watermarkText, W / 2, H / 2, {
    align:  'center',
    angle:  45,
    baseline: 'middle',
  });
  // Reset opacity
  doc.setGState(doc.GState({ opacity: 1 }));

  doc.save(`${invoice.invoiceNumber || 'Invoice'}_${isProforma ? 'Proforma' : 'Tax_Invoice'}.pdf`);
};
